import { api } from './api';

// Contrato completo em docs/CONTROLE_ANALISES_RACAO.md. A regra de avaliação
// (esperado ± tolerância -> ideal/abaixo/acima) mora só na API; a tela só
// mostra o que vem pronto.

export type NutrientKey =
  | 'moisture'
  | 'crude_protein'
  | 'ether_extract'
  | 'mineral_matter'
  | 'calcium'
  | 'phosphorus'
  | 'crude_fiber'
  | 'dgm'
  | 'dpg';

export type ReferencedNutrientKey = Exclude<NutrientKey, 'dgm' | 'dpg'>;

export type NutrientStatus = 'ideal' | 'below' | 'above' | 'no_reference';

export interface NutrientInfo {
  key: NutrientKey;
  label: string;
  unit: string;
  referenced: boolean;
}

// Mesmo catálogo da API (GET /feed-analysis/me devolve igual), fixo aqui
// para a tela não esperar a rede para montar colunas.
export const NUTRIENTS: NutrientInfo[] = [
  { key: 'moisture', label: 'Umidade', unit: '%', referenced: true },
  {
    key: 'crude_protein',
    label: 'Proteína Bruta',
    unit: '%',
    referenced: true,
  },
  {
    key: 'ether_extract',
    label: 'Extrato Etéreo',
    unit: '%',
    referenced: true,
  },
  {
    key: 'mineral_matter',
    label: 'Matéria Mineral',
    unit: '%',
    referenced: true,
  },
  { key: 'calcium', label: 'Cálcio', unit: '%', referenced: true },
  { key: 'phosphorus', label: 'Fósforo', unit: '%', referenced: true },
  { key: 'crude_fiber', label: 'Fibra Bruta', unit: '%', referenced: true },
  { key: 'dgm', label: 'DGM', unit: 'µm', referenced: false },
  { key: 'dpg', label: 'DPG', unit: '', referenced: false },
];

export const REFERENCED_NUTRIENTS = NUTRIENTS.filter(
  item => item.referenced,
) as (NutrientInfo & { key: ReferencedNutrientKey })[];

export type NutrientValues = Record<NutrientKey, number | null>;

export interface NutrientEvaluation {
  result: number | null;
  expected: number | null;
  min: number | null;
  max: number | null;
  difference: number | null;
  status: NutrientStatus | null;
}

export interface AppliedReference {
  id: string;
  formula_id: string;
  formula_code: string;
  valid_from: string;
  tolerance_above: number;
  tolerance_below: number;
}

export interface StatusSummary {
  ideal: number;
  below: number;
  above: number;
  no_reference: number;
}

export interface Evaluation {
  reference: AppliedReference | null;
  evaluation: Record<ReferencedNutrientKey, NutrientEvaluation>;
  summary: StatusSummary;
}

export interface FeedFormula {
  id: string;
  code: string;
  description: string | null;
  active: boolean;
}

export interface Feed {
  id: string;
  name: string;
  formula_id: string | null;
  formula_code: string | null;
  active: boolean;
  notes: string | null;
}

export interface FeedReference
  extends Record<ReferencedNutrientKey, number | null> {
  id: string;
  formula_id: string;
  formula_code: string;
  valid_from: string;
  tolerance_above: number | null;
  tolerance_below: number | null;
  notes: string | null;
  created_by_name: string | null;
  created_at: string;
}

export type FeedReferenceInput = Partial<
  Record<ReferencedNutrientKey, number | null>
> & {
  formula_id: string;
  valid_from: string; // AAAA-MM
  tolerance_above?: number | null;
  tolerance_below?: number | null;
  notes?: string | null;
};

export interface FeedAnalysisSettings {
  default_tolerance_above: number;
  default_tolerance_below: number;
}

export interface AppliedFeedReference {
  feed_id: string;
  feed_name: string;
  formula_id: string | null;
  formula_code: string | null;
  reference: AppliedReference | null;
  nutrients: Record<
    ReferencedNutrientKey,
    Pick<NutrientEvaluation, 'expected' | 'min' | 'max'>
  >;
}

export interface SampleFields extends Partial<NutrientValues> {
  sample_code: string;
  feed_id: string;
  item_name?: string | null;
  lot?: string | null;
  manufactured_on?: string | null;
  collected_on?: string | null;
  received_on?: string | null;
  released_at?: string | null;
  analysis_month: string; // AAAA-MM
  laboratory?: string | null;
}

export interface FeedSample extends NutrientValues, Evaluation {
  id: string;
  sample_code: string;
  feed_id: string;
  feed_name: string;
  formula_id: string | null;
  formula_code: string | null;
  item_name: string | null;
  lot: string | null;
  manufactured_on: string | null;
  collected_on: string | null;
  received_on: string | null;
  released_at: string | null;
  analysis_month: string; // AAAA-MM-01
  laboratory: string | null;
  report_url: string | null;
  report_original_name: string | null;
  analyst_note: string | null;
  reviewed_at: string | null;
  reviewed_by_name: string | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export type PreviewWarning =
  | 'missing_sample_code'
  | 'duplicate'
  | 'feed_not_found'
  | 'no_formula'
  | 'no_reference'
  | 'no_month'
  | 'no_results';

export interface PreviewRow extends Partial<NutrientValues> {
  sample_code?: string | null;
  item_name?: string | null;
  feed_id?: string | null;
  analysis_month?: string | null;
  manufactured_on?: string | null;
  collected_on?: string | null;
}

export interface PreviewResult extends Evaluation {
  sample_code: string | null;
  feed_id: string | null;
  feed_name: string | null;
  matched_by: 'id' | 'name' | null;
  formula_code: string | null;
  analysis_month: string | null;
  duplicate_of: {
    id: string;
    feed_name: string;
    analysis_month: string;
  } | null;
  warnings: PreviewWarning[];
}

export interface SamplesFilter {
  month?: string;
  from?: string;
  to?: string;
  feed_id?: string;
  search?: string;
  status?: 'out_of_range' | 'pending_review' | 'no_reference';
}

export type AlertType =
  | 'out_of_range'
  | 'recurring'
  | 'no_reference'
  | 'feed_without_formula';

export interface FeedAnalysisAlert {
  id: string;
  type: AlertType;
  severity: 'critical' | 'warning';
  feed_id: string;
  feed_name: string;
  sample_id: string | null;
  sample_code: string | null;
  nutrient: ReferencedNutrientKey | null;
  direction: 'below' | 'above' | null;
  result: number | null;
  expected: number | null;
  min: number | null;
  max: number | null;
  deviation: number | null;
  months: string[];
  message: string;
}

export interface FeedAnalysisAlerts {
  month: string;
  pending_review: number;
  alerts: FeedAnalysisAlert[];
}

export interface SummaryRow extends StatusSummary {
  samples: number;
  ideal_rate: number | null;
}

export interface FeedAnalysisIndicators {
  from: string;
  to: string;
  totals: SummaryRow;
  months: (SummaryRow & { month: string })[];
  feeds: (SummaryRow & { feed_id: string; feed_name: string })[];
  nutrients: (StatusSummary & {
    nutrient: ReferencedNutrientKey;
    label: string;
    ideal_rate: number | null;
  })[];
}

export interface FeedAnalysisAccess {
  isAdmin: boolean;
}

const BASE = '/feed-analysis';

// O pdf original é servido pelo backend (/uploads/...) ou pelo bucket.
export function absoluteReportUrl(url: string | null): string | null {
  if (!url) return null;
  if (/^https?:\/\//.test(url)) return url;
  return `${api.defaults.baseURL || ''}${url}`;
}

export const feedAnalysisService = {
  async me(): Promise<FeedAnalysisAccess> {
    const { data } = await api.get<FeedAnalysisAccess>(`${BASE}/me`);
    return data;
  },

  async listFormulas(): Promise<FeedFormula[]> {
    const { data } = await api.get<FeedFormula[]>(`${BASE}/formulas`);
    return data;
  },

  async saveFormula(
    input: Pick<FeedFormula, 'code' | 'description' | 'active'>,
    id?: string,
  ): Promise<FeedFormula> {
    const { data } = id
      ? await api.put<FeedFormula>(`${BASE}/formulas/${id}`, input)
      : await api.post<FeedFormula>(`${BASE}/formulas`, input);
    return data;
  },

  async listFeeds(): Promise<Feed[]> {
    const { data } = await api.get<Feed[]>(`${BASE}/feeds`);
    return data;
  },

  async saveFeed(
    input: Pick<Feed, 'name' | 'formula_id' | 'active' | 'notes'>,
    id?: string,
  ): Promise<Feed> {
    const { data } = id
      ? await api.put<Feed>(`${BASE}/feeds/${id}`, input)
      : await api.post<Feed>(`${BASE}/feeds`, input);
    return data;
  },

  async listReferences(): Promise<FeedReference[]> {
    const { data } = await api.get<FeedReference[]>(`${BASE}/references`);
    return data;
  },

  async appliedReferences(month: string): Promise<AppliedFeedReference[]> {
    const { data } = await api.get<AppliedFeedReference[]>(
      `${BASE}/references/applied`,
      { params: { month } },
    );
    return data;
  },

  async saveReference(
    input: FeedReferenceInput,
    id?: string,
  ): Promise<FeedReference> {
    const { data } = id
      ? await api.put<FeedReference>(`${BASE}/references/${id}`, input)
      : await api.post<FeedReference>(`${BASE}/references`, input);
    return data;
  },

  async getSettings(): Promise<FeedAnalysisSettings> {
    const { data } = await api.get<FeedAnalysisSettings>(`${BASE}/settings`);
    return data;
  },

  async updateSettings(
    input: FeedAnalysisSettings,
  ): Promise<FeedAnalysisSettings> {
    const { data } = await api.put<FeedAnalysisSettings>(
      `${BASE}/settings`,
      input,
    );
    return data;
  },

  async preview(rows: PreviewRow[]): Promise<PreviewResult[]> {
    const { data } = await api.post<PreviewResult[]>(
      `${BASE}/samples/preview`,
      { samples: rows },
    );
    return data;
  },

  async listSamples(filter: SamplesFilter): Promise<FeedSample[]> {
    const { data } = await api.get<FeedSample[]>(`${BASE}/samples`, {
      params: filter,
    });
    return data;
  },

  async getSample(id: string): Promise<FeedSample> {
    const { data } = await api.get<FeedSample>(`${BASE}/samples/${id}`);
    return data;
  },

  async createSample(input: SampleFields): Promise<FeedSample> {
    const { data } = await api.post<FeedSample>(`${BASE}/samples`, input);
    return data;
  },

  async updateSample(id: string, input: SampleFields): Promise<FeedSample> {
    const { data } = await api.put<FeedSample>(`${BASE}/samples/${id}`, input);
    return data;
  },

  async reviewSample(id: string, analystNote: string): Promise<FeedSample> {
    const { data } = await api.post<FeedSample>(
      `${BASE}/samples/${id}/review`,
      { analyst_note: analystNote },
    );
    return data;
  },

  async deleteSample(id: string): Promise<void> {
    await api.delete(`${BASE}/samples/${id}`);
  },

  async uploadReport(id: string, file: File): Promise<FeedSample> {
    const form = new FormData();
    form.append('report', file);
    const { data } = await api.post<FeedSample>(
      `${BASE}/samples/${id}/report`,
      form,
    );
    return data;
  },

  async indicators(params: {
    from: string;
    to: string;
    feed_id?: string;
  }): Promise<FeedAnalysisIndicators> {
    const { data } = await api.get<FeedAnalysisIndicators>(
      `${BASE}/indicators`,
      { params },
    );
    return data;
  },

  async alerts(month: string): Promise<FeedAnalysisAlerts> {
    const { data } = await api.get<FeedAnalysisAlerts>(`${BASE}/alerts`, {
      params: { month },
    });
    return data;
  },
};

// Mensagem de erro da API (AppError devolve { message }).
export function apiErrorMessage(error: unknown, fallback: string): string {
  const message = (error as { response?: { data?: { message?: unknown } } })
    ?.response?.data?.message;
  return typeof message === 'string' && message ? message : fallback;
}
